import { useState, useEffect } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import { getApiBaseUrl } from '../utils/api';

const RetailOrderPage = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [lastCreatedOrderReceipt, setLastCreatedOrderReceipt] = useState(null);
  const [phone, setPhone] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [item, setItem] = useState({
    category: '',
    model: '',
    modelName: '',
    modelNumber: '',
    unitPrice: 0,
    currentStock: 0,
    qty: 1,
    totalValue: 0,
    advance: 0,
    colorRequest: ''
  });

  // Fetch inventory dropdown on component mount
  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/inventory/dropdown?type=retail`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setInventory(data);
        }
      } catch (error) {
        toast.error('Failed to load inventory');
        console.error('Error fetching inventory:', error);
      }
    };
    fetchInventory();
  }, []);

  // Debug state changes
  useEffect(() => {
    console.log('State changed - lastCreatedOrderReceipt:', !!lastCreatedOrderReceipt);
  }, [lastCreatedOrderReceipt]);

  // Calculate totals
  const grandTotal = item.totalValue;
  const totalAdvance = item.advance;
  const balance = grandTotal - totalAdvance;

  // Handle category selection
  const handleCategoryChange = (category) => {
    setItem({
      ...item,
      category: category,
      model: '', // Reset model selection when category changes
      modelName: '',
      modelNumber: '',
      unitPrice: 0,
      currentStock: 0,
      totalValue: 0,
      advance: 0
    });
  };

  // Handle model selection
  const handleModelChange = (modelId) => {
    const selectedModel = inventory.find(inv => inv._id === modelId);
    if (selectedModel) {
      setItem({
        ...item,
        model: modelId,
        modelName: selectedModel.modelName,
        modelNumber: selectedModel.modelNumber,
        unitPrice: selectedModel.price,
        currentStock: selectedModel.currentStock,
        totalValue: selectedModel.price * item.qty,
        advance: selectedModel.price * item.qty // For retail, advance = total
      });
    }
  };

  // Filter inventory by category
  const getFilteredInventory = (category) => {
    if (!category) return [];
    return inventory.filter(item => item.size === category.toLowerCase());
  };

  // Handle advance change
  const handleAdvanceChange = (advance) => {
    setItem({
      ...item,
      advance: parseFloat(advance) || 0
    });
  };

  // Handle color request change
  const handleColorRequestChange = (colorRequest) => {
    setItem({
      ...item,
      colorRequest: colorRequest
    });
  };

  // Validate form
  const validateForm = () => {
    if (!customerName.trim()) {
      toast.error('Customer name is required');
      return false;
    }
    if (!phone.trim()) {
      toast.error('Phone number is required');
      return false;
    }
    // Indian mobile validation: 10 digits starting with 6-9
    if (!/^[6-9]\d{9}$/.test(phone.trim())) {
      toast.error('Please enter a valid 10-digit Indian mobile number');
      return false;
    }
    if (!date) {
      toast.error('Date is required');
      return false;
    }

    // Check if item has category and model selected
    if (!item.category) {
      toast.error('Please select a category');
      return false;
    }
    if (!item.model) {
      toast.error('Please select a model');
      return false;
    }

    // Find the inventory item to check availability
    const inventoryItem = inventory.find(inv => inv._id === item.model);
    if (inventoryItem && !inventoryItem.isAvailable) {
      toast.error(`Cannot select ${inventoryItem.modelName} - item is out of stock`);
      return false;
    }

    if (item.qty > item.currentStock) {
      toast.error(`Insufficient stock for ${item.modelName}. Available: ${item.currentStock}`);
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('Form submitted');
    console.log('Customer name:', customerName);
    console.log('Phone:', phone);
    console.log('Date:', date);
    console.log('Item:', item);

    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    setLoading(true);
    console.log('Starting order creation...');

    try {
      // First check authentication
      console.log('Checking authentication...');
      const authResponse = await fetch(`${getApiBaseUrl()}/api/user`, {
        credentials: 'include'
      });
      console.log('Auth check - Status:', authResponse.status);

      if (!authResponse.ok) {
        throw new Error('Not authenticated. Please log in again.');
      }

      const user = await authResponse.json();
      console.log('User authenticated:', user ? 'YES' : 'NO');

      const orderData = {
        type: 'retail',
        customerName: customerName.trim(),
        phone: phone.trim(),
        date,
        items: [{
          model: item.model,
          qty: item.qty,
          advance: item.advance,
          colorRequest: item.colorRequest
        }]
      };

      console.log('Sending order data:', orderData);
      console.log('Making fetch request to: http://localhost:5000/api/orders');

      let response;
      try {
        console.log('Starting fetch request...');

        response = await fetch(`${getApiBaseUrl()}/api/orders`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(orderData)
        });

        console.log('Fetch completed, response received');
      } catch (fetchError) {
        console.error('Fetch request failed:', fetchError);
        throw fetchError;
      }

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        const result = await response.json();
        console.log('Order created successfully:', result);
        console.log('Result keys:', Object.keys(result));
        console.log('receiptText in result:', result.receiptText ? 'YES' : 'NO');
        console.log('receiptText length:', result.receiptText ? result.receiptText.length : 0);

        try {
          // Show success notification with order details
          toast.success(`🛒 Retail Order ${result.order.orderNo} Created Successfully!`, {
            duration: 6000,
            icon: '✅',
            style: {
              background: '#dcfce7',
              color: '#166534',
              border: '2px solid #16a34a',
              fontSize: '14px',
              fontWeight: 'bold'
            }
          });

          // Additional notification about PDF download
          // setTimeout(() => {
          //   toast.success('📄 Receipt PDF Downloaded Automatically', {
          //     duration: 4000,
          //     icon: '📋',
          //     style: {
          //       background: '#e0f2fe',
          //       color: '#0c4a6e',
          //       border: '1px solid #0284c7'
          //     }
          //   });
          // }, 1000);

          // Store receipt for download
          if (result.pdfBase64) {
            setLastCreatedOrderReceipt({
              content: result.pdfBase64,
              orderNo: result.order.orderNo,
              isPdf: true
            });
          }

          // Don't reset receipt state here - let it persist for download
          // Reset form (but keep receipt data)
          setCustomerName('');
          setPhone('');
          setDate(new Date().toISOString().split('T')[0]);
          // Keep receiptText and orderNo for download button
          setItem({
            category: '',
            model: '',
            modelName: '',
            modelNumber: '',
            unitPrice: 0,
            currentStock: 0,
            qty: 1,
            totalValue: 0,
            advance: 0,
            colorRequest: ''
          });
        } catch (successError) {
          console.error('Error in success handling:', successError);
          // Still show success even if there's an error in handling
          toast.success('🛒 Retail Order Created Successfully!', {
            duration: 6000,
            icon: '✅',
            style: {
              background: '#dcfce7',
              color: '#166534',
              border: '2px solid #16a34a',
              fontSize: '14px',
              fontWeight: 'bold'
            }
          });
        }
      } else {
        const error = await response.json();
        console.log('Order creation failed:', error);
        toast.error(error.error || 'Failed to create order');
      }
    } catch (error) {
      console.log('Network error:', error);
      toast.error('Failed to create order');
      console.error('Error creating order:', error);
    } finally {
      setLoading(false);
      console.log('Order creation process completed');
    }
  };

  // Download receipt function (PDF or text fallback)
  const downloadReceipt = (content, orderNo) => {
    try {
      // Check if it's PDF (base64) or text
      if (lastCreatedOrderReceipt?.isPdf) {
        // PDF download from base64
        const byteCharacters = atob(content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Receipt_${orderNo}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // Text download (fallback)
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Receipt_${orderNo}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      toast.success('📄 Receipt Downloaded Successfully!', {
        duration: 3000,
        icon: '✅'
      });
    } catch (error) {
      console.error('Error downloading receipt:', error);
      toast.error('Failed to download receipt');
    }
  };

  return (
    <>
      <Toaster position="top-right" />
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">
              🛒 New Retail Order
            </h1>
            <p className="text-gray-600">Create a new retail order for Ganesh idols</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-xl p-6">
            {/* Customer Details */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Customer Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter customer name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="10-digit mobile number"
                    pattern="[6-9][0-9]{9}"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Order Item */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Order Item</h2>

              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category *
                    </label>
                    <select
                      value={item.category}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    >
                      <option value="">Select category</option>
                      <option value="small">Small (1-20)</option>
                      <option value="medium">Medium (21-30)</option>
                      <option value="large">Large (31-40)</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Model *
                    </label>
                    <select
                      value={item.model}
                      onChange={(e) => handleModelChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                      disabled={!item.category}
                    >
                      <option value="">
                        {item.category ? 'Select a model' : 'Select category first'}
                      </option>
                      {getFilteredInventory(item.category).map(inv => (
                        <option
                          key={inv._id}
                          value={inv._id}
                          disabled={!inv.isAvailable}
                          className={!inv.isAvailable ? 'text-red-600 bg-red-50' : ''}
                        >
                          {inv.isAvailable ? (
                            `${inv.modelName} - ${inv.modelNumber} (₹${inv.price.toLocaleString('en-IN')}, Stock: ${inv.currentStock})`
                          ) : (
                            `🚫 ${inv.modelName} - ${inv.modelNumber} (₹${inv.price.toLocaleString('en-IN')}) - OUT OF STOCK`
                          )}
                        </option>
                      ))}
                    </select>

                    {/* Model Photo Display */}
                    {item.model && (() => {
                      const selectedModel = inventory.find(inv => inv._id === item.model);
                      return selectedModel?.photo ? (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                          <div className="flex items-center gap-3">
                            <img
                              src={selectedModel.photo}
                              alt={`${selectedModel.modelName} preview`}
                              className="w-16 h-16 object-cover rounded-lg border shadow-sm"
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-800">
                                {selectedModel.modelName}
                              </p>
                              <p className="text-xs text-gray-600">
                                Model {selectedModel.modelNumber}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantity
                    </label>
                    <input
                      type="number"
                      value={item.qty}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">Retail orders are limited to 1 quantity</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit Price
                    </label>
                    <input
                      type="text"
                      value={`₹${item.unitPrice.toLocaleString('en-IN')}`}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Value
                    </label>
                    <input
                      type="text"
                      value={`₹${item.totalValue.toLocaleString('en-IN')}`}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 font-semibold"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Advance Payment
                    </label>
                    <input
                      type="number"
                      value={item.advance}
                      onChange={(e) => handleAdvanceChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Color Request
                    </label>
                    <textarea
                      value={item.colorRequest}
                      onChange={(e) => handleColorRequestChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      rows="2"
                      placeholder="Any special color requirements..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="mb-6 bg-green-50 p-4 rounded-lg">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Order Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div className="bg-white p-3 rounded border">
                  <p className="text-sm text-gray-600">Grand Total</p>
                  <p className="text-2xl font-bold text-green-600">₹{grandTotal.toLocaleString('en-IN')}</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-sm text-gray-600">Total Advance</p>
                  <p className="text-2xl font-bold text-blue-600">₹{totalAdvance.toLocaleString('en-IN')}</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-sm text-gray-600">Balance Due</p>
                  <p className="text-2xl font-bold text-red-600">₹{balance.toLocaleString('en-IN')}</p>
                </div>
              </div>
            </div>

            {/* Submit Button and Download Link */}
            <div className="flex justify-center items-center gap-4">
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Creating Order...' : 'Create Retail Order'}
              </button>

              {lastCreatedOrderReceipt && (
                <button
                  type="button"
                  onClick={() => downloadReceipt(lastCreatedOrderReceipt.content, lastCreatedOrderReceipt.orderNo)}
                  className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 animate-pulse"
                >
                  📄 Download {lastCreatedOrderReceipt.isPdf ? 'PDF' : 'Text'} Receipt
                </button>
              )}
            </div>
          </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default RetailOrderPage;
